import { fetchApi } from "./api";

/**
 * Shopify-backed research store. Every call goes through the store-api Lambda —
 * the Storefront token stays server-side and product rows are served from the
 * `research_products` cache, so page loads never wait on Shopify.
 */

export type ResearchProduct = {
  id: string;
  shopify_product_id: string;
  shopify_variant_id: string;
  handle: string;
  title: string;
  description_html: string | null;
  price_cents: number;
  currency: string;
  image_url: string | null;
  pages: number | null;
  publish_date: string | null;
  details: string[];
  details_description: string | null;
  summary_pdf_url: string | null;
  display_order: number;
};

/** Shopify product status. Rows synced before stage 2 may not carry it. */
export type ProductStatus = "ACTIVE" | "ARCHIVED" | "DRAFT";

export type ResearchProductAdmin = ResearchProduct & {
  is_active: boolean;
  synced_at: string | null;
  created_at: string;
  updated_at: string;
  status?: ProductStatus;
};

export type CartLine = {
  lineId: string;
  variantId: string;
  title: string;
  priceCents: number;
  currency: string;
  imageUrl: string | null;
  pages: number | null;
};

export type CartPayload = {
  cartId: string;
  checkoutUrl: string;
  lines: CartLine[];
  subtotalCents: number;
  totalCents: number;
  currency: string;
  /** true when the variant was already in the cart, so nothing was added. */
  alreadyInCart?: boolean;
};

export type SyncProductsResult = { synced: number; deactivated: number };

export const listResearchProducts = () =>
  fetchApi<ResearchProduct[]>("store", "listResearchProducts", {}, { auth: false });

export const getCart = (opts: { data: { cartId: string } }) =>
  fetchApi<CartPayload>("store", "getCart", opts.data, { auth: false });

export const addToCart = (opts: { data: { cartId?: string; variantId: string } }) =>
  fetchApi<CartPayload>("store", "addToCart", opts.data, { auth: false });

export const removeFromCart = (opts: { data: { cartId: string; lineId: string } }) =>
  fetchApi<CartPayload>("store", "removeFromCart", opts.data, { auth: false });

export const listResearchProductsAdmin = () =>
  fetchApi<ResearchProductAdmin[]>("store", "listResearchProductsAdmin", {});

export const syncProducts = () => fetchApi<SyncProductsResult>("store", "syncProducts", {});

/* ---------------------------------------------------------------- stage 2 */
/* Product editing. Every write goes to the Shopify Admin API through
 * store-api and ends with a targeted resync into research_products, so the
 * row these return is already the refreshed cache row. Until the customer
 * supplies the Admin token the writes fail with SHOPIFY_ADMIN_UNCONFIGURED —
 * the UI probes `getShopifyAdminStatus` first and disables editing. */

/** Raised by every write action while the `miinews/shopify-admin` secret is a placeholder. */
export const SHOPIFY_ADMIN_UNCONFIGURED = "SHOPIFY_ADMIN_UNCONFIGURED";

export type ShopifyAdminStatus = { configured: boolean; shop?: string };

export type SaveProductInput = {
  /** Omit to create a new Shopify product. */
  id?: string;
  title: string;
  price_cents: number;
  status?: ProductStatus;
  /**
   * Every field below is three-state. Omitting the key leaves that field
   * untouched; null or "" clears it; anything else writes it. The editor omits
   * what the admin did not touch, because the row it loads from is a cache and
   * echoing a cached blank back would clear whatever the cache failed to
   * mirror.
   */
  description_html?: string | null;
  pages?: number | null;
  /** Free text, 32 chars max — the live catalog is not consistently YYYY-MM. */
  publish_date?: string | null;
  details?: string[];
  details_description?: string | null;
};

export type SaveProductResult = {
  /**
   * The refreshed cache row. Typed nullable, but the targeted resync writes it
   * immediately before reading it back, so in practice it is always present.
   */
  product: ResearchProductAdmin | null;
  /** Shopify GID of the product that was written. */
  productId: string;
};

/** Cover images and summary PDFs are attached asynchronously by Shopify. */
export type ProductFileKind = "cover" | "pdf";

export type ArchiveProductResult = {
  product: ResearchProductAdmin | null;
  archived: boolean;
};

export type AttachProductFileResult = {
  fileId: string;
  /** Normally UPLOADED or PROCESSING — ingestion has only just started. */
  fileStatus: string;
  kind: ProductFileKind;
};

export type DetachProductFileResult = {
  product: ResearchProductAdmin | null;
  detached: boolean;
};

export type CheckProductFileResult = {
  /**
   * Shopify reports UPLOADED, PROCESSING, READY or FAILED. Anything that is
   * not READY or FAILED means ingestion is still running.
   */
  status: string;
  /** The refreshed row once the file is READY and re-synced; null before then. */
  product: ResearchProductAdmin | null;
  /** Shopify's own file-error messages, populated only on FAILED. */
  errors: string[];
};

export const getShopifyAdminStatus = () =>
  fetchApi<ShopifyAdminStatus>("store", "getShopifyAdminStatus", {});

export const saveProduct = (opts: { data: SaveProductInput }) =>
  fetchApi<SaveProductResult>("store", "saveProduct", opts.data);

export const archiveProduct = (opts: { data: { id: string } }) =>
  fetchApi<ArchiveProductResult>("store", "archiveProduct", opts.data);

export const attachProductFile = (opts: {
  data: { productId: string; kind: ProductFileKind; sourceUrl: string };
}) => fetchApi<AttachProductFileResult>("store", "attachProductFile", opts.data);

export const checkProductFile = (opts: {
  data: { fileId: string; productId: string; kind: ProductFileKind };
}) => fetchApi<CheckProductFileResult>("store", "checkProductFile", opts.data);

/**
 * Drop the cover or PDF reference. The file itself stays in Shopify Files, so
 * this is reversible by re-attaching and it cannot affect another product
 * pointing at the same file.
 */
export const detachProductFile = (opts: {
  data: { productId: string; kind: ProductFileKind };
}) => fetchApi<DetachProductFileResult>("store", "detachProductFile", opts.data);
